/**
 * Phase 131C — Mark Highlight Panel
 * ---------------------------------
 * Big, obvious, mobile-first one-tap UI for broadcasters to seed the
 * highlight event substrate. Each tap:
 *   1. POSTs /api/livex/streams/{id}/highlights → server records the marker
 *      (anchored to match-time) and auto-queues a clip intent (-10s/+20s).
 *   2. Broadcasts via LiveKit data channel topic 'livex-highlight' so all
 *      live viewers see a brief in-room flash banner.
 *   3. Triggers a confirmation toast + haptic buzz on mobile.
 *
 * UX doctrine (Phase 131A): high-contrast text, thick #22314F borders, 16px+
 * inputs, no opacity tricks. Type tiles are large + clearly tappable.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocalParticipant } from '@livekit/components-react';
import {
  Star, Check, Trash2, Loader2, AlertTriangle, History,
} from 'lucide-react';

const HIGHLIGHT_TYPES = [
  { key: 'goal',          emoji: '⚽', label: 'Goal' },
  { key: 'wicket',        emoji: '🏏', label: 'Wicket' },
  { key: 'great_play',    emoji: '⭐', label: 'Great Play' },
  { key: 'celebration',   emoji: '👏', label: 'Celebrate' },
  { key: 'crowd',         emoji: '🔥', label: 'Crowd' },
  { key: 'announcement',  emoji: '📢', label: 'Announce' },
];

export default function LivexHighlightPanel({
  streamId,
  apiClient,
  apiPathBase, // '/livex/streams/{id}'
}) {
  const { localParticipant } = useLocalParticipant();
  const [highlights, setHighlights] = useState([]);
  const [adding, setAdding] = useState(null); // key of the type being added (for spinner)
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null); // { type, label }
  const toastTimerRef = useRef(null);

  const typeMap = useMemo(
    () => Object.fromEntries(HIGHLIGHT_TYPES.map((t) => [t.key, t])),
    [],
  );

  // Initial load — show any markers already attached to this stream.
  useEffect(() => {
    if (!streamId || !apiClient) return;
    let active = true;
    apiClient
      .get(`${apiPathBase}/highlights`)
      .then((r) => active && setHighlights(r.data?.entries || []))
      .catch(() => { /* ignore — empty list is OK */ });
    return () => { active = false; };
  }, [streamId, apiClient, apiPathBase]);

  const broadcastToViewers = useCallback((highlight) => {
    if (!localParticipant) return;
    try {
      const t = typeMap[highlight.type] || { emoji: '⭐', label: highlight.type };
      const payload = new TextEncoder().encode(JSON.stringify({
        emoji: t.emoji,
        label: t.label,
        match_time_label: highlight.match_time_label,
        id: highlight.id,
      }));
      localParticipant.publishData(payload, { reliable: true, topic: 'livex-highlight' });
    } catch { /* best-effort */ }
  }, [localParticipant, typeMap]);

  const showToast = (highlight) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ type: highlight.type, label: highlight.match_time_label });
    toastTimerRef.current = setTimeout(() => setToast(null), 2600);
  };

  const addHighlight = async (typeKey) => {
    if (adding) return;
    setAdding(typeKey);
    setError('');
    try {
      const { data } = await apiClient.post(`${apiPathBase}/highlights`, { type: typeKey });
      setHighlights((prev) => [
        ...prev.filter((h) => h.id !== data.id),
        data,
      ].sort((a, b) => a.match_time_seconds - b.match_time_seconds));
      broadcastToViewers(data);
      showToast(data);
      if (typeof navigator !== 'undefined' && navigator.vibrate) {
        try { navigator.vibrate(40); } catch { /* ignore */ }
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to add highlight');
    } finally {
      setAdding(null);
    }
  };

  const removeHighlight = async (hid) => {
    if (!window.confirm('Remove this highlight? (Will not be in the replay chapters.)')) return;
    try {
      await apiClient.delete(`${apiPathBase}/highlights/${hid}`);
      setHighlights((prev) => prev.filter((h) => h.id !== hid));
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to remove highlight');
    }
  };

  return (
    <div className="bg-[#07111F] border-2 border-[#22314F] rounded-xl overflow-hidden" data-testid="livex-highlight-panel">
      {/* Header */}
      <div className="px-4 py-3 border-b-2 border-[#22314F] bg-[#0c1a2e] flex items-center gap-2">
        <Star className="h-4 w-4 text-[#FF5A1F]" />
        <div className="livex-label" style={{ marginBottom: 0 }}>Mark Highlight</div>
        <span
          className="ml-auto inline-flex items-center gap-1 text-[11px] font-bold text-[#94A3B8] tabular-nums"
          data-testid="livex-highlight-count"
        >
          <History className="h-3 w-3" /> {highlights.length} marked
        </span>
      </div>

      {/* Type tiles — large, mobile-first, 3-col on mobile / 6-col on desktop */}
      <div className="p-3 grid grid-cols-3 sm:grid-cols-6 gap-2">
        {HIGHLIGHT_TYPES.map((t) => {
          const isLoading = adding === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => addHighlight(t.key)}
              disabled={adding !== null}
              data-testid={`livex-highlight-${t.key}`}
              className="flex flex-col items-center justify-center gap-1.5 px-2 py-3.5 bg-[#07111F] border-2 border-[#22314F] hover:border-[#FF5A1F] hover:bg-[#0c1a2e] rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed active:scale-95"
            >
              <span className="text-2xl leading-none" aria-hidden>
                {isLoading ? <Loader2 className="h-6 w-6 animate-spin text-[#FF5A1F]" /> : t.emoji}
              </span>
              <span className="text-[11px] font-bold text-white uppercase tracking-[0.05em] leading-tight text-center">
                {t.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Toast confirmation */}
      {toast && (
        <div
          className="px-4 py-3 bg-emerald-950 border-y-2 border-emerald-700 flex items-center gap-2"
          data-testid="livex-highlight-toast"
        >
          <Check className="h-4 w-4 text-emerald-400 flex-shrink-0" />
          <span className="text-sm font-bold text-emerald-100">
            {typeMap[toast.type]?.emoji}{' '}
            {typeMap[toast.type]?.label || toast.type} marked at{' '}
            <span className="font-mono tabular-nums">{toast.label}</span>
          </span>
        </div>
      )}

      {error && (
        <div className="px-4 py-2.5 bg-rose-950 border-y-2 border-rose-700 text-rose-100 text-sm font-bold flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" /> {error}
        </div>
      )}

      {/* Recent timeline (most recent first) */}
      {highlights.length > 0 && (
        <div className="max-h-[220px] overflow-y-auto" data-testid="livex-highlight-list">
          {[...highlights].reverse().map((h) => {
            const t = typeMap[h.type] || { emoji: '⭐', label: h.type };
            return (
              <div
                key={h.id}
                className="flex items-center gap-3 px-4 py-2.5 border-t-2 border-[#22314F] hover:bg-[#0c1a2e] group"
                data-testid={`livex-highlight-row-${h.id}`}
              >
                <span className="text-2xl flex-shrink-0" aria-hidden>{t.emoji}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-white truncate">
                    {t.label}
                    {h.note ? <span className="font-medium text-[#CBD5E1] ml-2">— {h.note}</span> : null}
                  </div>
                  <div className="text-[11px] font-medium text-[#94A3B8]">
                    <span className="font-mono tabular-nums">{h.match_time_label}</span>
                    {h.created_by_name && ` · by ${h.created_by_name}`}
                    {' · clip queued'}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeHighlight(h.id)}
                  data-testid={`livex-highlight-delete-${h.id}`}
                  className="opacity-100 sm:opacity-0 group-hover:opacity-100 flex-shrink-0 p-1.5 text-[#94A3B8] hover:text-rose-300 hover:bg-rose-950/30 rounded transition-all"
                  title="Remove this highlight"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
